import type { SkillSummary } from "@tabbio/types";
import { buildPublicSkillShareUrl } from '../../lib/public-web';
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useTranslation } from "react-i18next";
import { WebDialog } from "@/components/common/WebDialog.web";
import { NativeButton } from "@/components/common/NativeButton";
import { useSkillInstallAction } from "./use-skill-install-action";
import { SkillCatalogIcon } from "./SkillCatalogIcon";
import { TabbioVerifiedBadge } from "./TabbioVerifiedBadge";
import {
  formatSkillCount,
  formatSkillCommand,
  prettySkillName,
} from "./skill-name";

/**
 * Web twin of the native install bottom sheet, hosted in the shared WebDialog
 * shell (which itself degrades to a bottom sheet at phone widths), so /skills
 * on desktop gets the standard centered dialog instead of a web-rendered
 * mobile sheet.
 */
export function SkillInstallSheet({
  skill,
  visible,
  onClose,
}: {
  skill: SkillSummary | null;
  visible: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const { run, isPending, installLocked } = useSkillInstallAction({
    skill,
    onClose,
  });
  if (!skill) return null;
  const author = skill.authorName || t("skills.byTabbioName");
  const share = async () => {
    const url = buildPublicSkillShareUrl(skill.skillId);
    if (navigator.share) await navigator.share({ title: skill.name, url });
    else await navigator.clipboard.writeText(url);
  };
  return (
    <WebDialog
      visible={visible}
      onClose={onClose}
      title={formatSkillCommand(skill.skillId)}
      subtitle={t("skills.by", { author })}
      maxWidth={480}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <NativeButton
              title={
                skill.installed
                  ? t("skills.uninstall")
                  : installLocked
                    ? t("skills.installUpgrade")
                    : t("skills.install")
              }
              onPress={() => void run()}
              loading={isPending}
              color="contrast"
              size="lg"
              pill
              block
              style={{ flex: 1 }}
            />
            <NativeButton
              title={t("actions.share")}
              onPress={() => void share()}
              variant="outline"
              size="lg"
              pill
              iconLeft={<ShareIcon size={18} />}
            />
          </div>
          <div
            style={{
              textAlign: "center",
              fontSize: 13,
              color: "var(--text-tertiary)",
            }}
          >
            {t("skills.usageCount", {
              value: formatSkillCount(skill.usageCount),
            })}
          </div>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <SkillCatalogIcon skill={skill} size={64} />
          <div style={{ minWidth: 0 }}>
            <div
              dir="auto"
              style={{
                fontSize: 22,
                lineHeight: "28px",
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              {prettySkillName(skill.name)}
            </div>
            {skill.verified ? <TabbioVerifiedBadge /> : null}
          </div>
        </div>
        <p
          dir="auto"
          style={{
            margin: 0,
            fontSize: 16,
            lineHeight: "24px",
            color: "var(--text-secondary)",
          }}
        >
          {skill.description}
        </p>
        {skill.tags.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {skill.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                style={{
                  padding: "7px 12px",
                  borderRadius: 999,
                  background: "var(--hover)",
                  color: "var(--text-secondary)",
                  fontSize: 13,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </WebDialog>
  );
}
